// src/components/features/content/form-steps/DistributionStep.jsx
import React from 'react';
import {
  Box, Grid, Chip, Typography, Slider, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, TextField
} from '@mui/material';
import { 
  Public as PublicIcon,
  PeopleAlt as AudienceIcon,
  Share as ShareIcon,
  Place as PlaceIcon,
  Interests as InterestsIcon,
  Wc as GenderIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

// Import shared form components
import { TextField as DesignTextField, Select as DesignSelect } from '../../../../design-system';
import { FormSection } from '../../../../design-system/components/forms';
import { EntityBasicInfoForm } from '../../../shared/forms';

// Import constants
import { PLATFORM_OPTIONS } from '../form-utils/contentFormData';

const DistributionStep = ({ formik }) => {
  // Gender options
  const GENDER_OPTIONS = [
    { value: 'all', label: 'All Genders' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
  ];
  
  // Common interest suggestions for autocomplete
  const INTEREST_SUGGESTIONS = [
    'Technology', 'Travel', 'Fashion', 'Food', 'Fitness', 'Health', 'Beauty', 
    'Music', 'Movies', 'Sports', 'Gaming', 'Photography', 'Art', 'Design', 
    'Education', 'Business', 'Finance', 'Real Estate', 'Luxury', 'Family'
  ];

  // Initialize or get target audience
  const targetAudience = formik.values.targetAudience || {
    age: { min: 18, max: 65 },
    gender: 'all',
    interests: [],
    locations: []
  };
  
  // Ensure values are arrays for multiple select fields
  if (!Array.isArray(targetAudience.interests)) {
    targetAudience.interests = [];
  }
  
  if (!Array.isArray(targetAudience.locations)) {
    targetAudience.locations = [];
  };
  
  // Handle age range change with slider
  const handleAgeRangeChange = (event, newValue) => {
    const currentTargetAudience = { ...formik.values.targetAudience } || {};
    formik.setFieldValue('targetAudience', {
      ...currentTargetAudience,
      age: {
        ...currentTargetAudience.age,
        min: newValue[0],
        max: newValue[1]
      }
    });
  };

  // Handle gender change
  const handleGenderChange = (event) => {
    const currentTargetAudience = { ...formik.values.targetAudience } || {};
    formik.setFieldValue('targetAudience', {
      ...currentTargetAudience,
      gender: event.target.value
    });
  };

  // Handle interests change
  const handleInterestsChange = (newInterests) => {
    const currentTargetAudience = { ...formik.values.targetAudience } || {};
    formik.setFieldValue('targetAudience', {
      ...currentTargetAudience,
      interests: newInterests
    });
  };

  // Handle locations change
  const handleLocationsChange = (newLocations) => {
    const currentTargetAudience = { ...formik.values.targetAudience } || {};
    formik.setFieldValue('targetAudience', {
      ...currentTargetAudience,
      locations: newLocations
    });
  };

  // Handle platform selection
  const handlePlatformToggle = (platform) => {
    const currentDistributions = [...(formik.values.distributions || [])];
    const isSelected = currentDistributions.some(dist => dist.platform === platform.value);
    
    if (isSelected) {
      // Remove platform
      const updatedDistributions = currentDistributions.filter(dist => dist.platform !== platform.value);
      formik.setFieldValue('distributions', updatedDistributions);
    } else {
      // Add platform
      currentDistributions.push({
        platform: platform.value,
        status: 'draft'
      });
      formik.setFieldValue('distributions', currentDistributions);
    }
  };

  // Render platforms section
  const renderPlatformsSection = () => (
    <FormSection
      title="Platform Distribution"
      description="Select which platforms this content will be distributed to. You can schedule and configure each platform after saving."
      icon={<ShareIcon />}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {PLATFORM_OPTIONS.map(platform => {
          const isSelected = formik.values.distributions?.some(
            dist => dist.platform === platform.value
          );
          
          return (
            <Chip
              key={platform.value}
              label={platform.label}
              clickable
              color={isSelected ? 'primary' : 'default'}
              onClick={() => handlePlatformToggle(platform)}
              sx={{ m: 0.5 }}
            />
          );
        })}
      </Box>
    </FormSection>
  );

  return (
    <>
      {/* Age Range Section - Using Slider similar to CampaignTargetAudienceForm */}
      <FormSection
        title="Age Range *"
        description="Set the target age group for your content (required)"
        icon={<AudienceIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Age Range: {targetAudience.age?.min || 18} - {targetAudience.age?.max || 65}
            </Typography>
            <Slider
              value={[
                targetAudience.age?.min || 18,
                targetAudience.age?.max || 65
              ]}
              onChange={handleAgeRangeChange}
              valueLabelDisplay="auto"
              min={13}
              max={65}
              step={1}
              sx={{ mt: 2 }}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Gender Section - Using Select similar to CampaignTargetAudienceForm */}
      <FormSection
        title="Gender *"
        description="Specify the target gender for your content (required)"
        icon={<GenderIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="gender-label" required>
                Gender *
              </InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                value={targetAudience.gender || 'all'}
                onChange={handleGenderChange}
                label="Gender"
              >
                {GENDER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>

      {/* Interests Section - Using Autocomplete similar to CampaignTargetAudienceForm */}
      <FormSection
        title="Interests"
        description="Add relevant interests for your target audience"
        icon={<InterestsIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="interests"
              options={INTEREST_SUGGESTIONS}
              freeSolo
              value={Array.isArray(targetAudience.interests) ? targetAudience.interests : []}
              onChange={(event, newValue) => handleInterestsChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    icon={<InterestsIcon />}
                    label={option}
                    {...getTagProps({ index })}
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Interests *"
                  placeholder="Add interest and press Enter"
                  fullWidth
                  required
                  helperText="Topics and activities the audience is interested in (required)"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Locations Section - Using Autocomplete similar to CampaignTargetAudienceForm */}
      <FormSection
        title="Locations"
        description="Add target geographic locations for your content"
        icon={<PlaceIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="locations"
              options={[]}
              freeSolo
              value={Array.isArray(targetAudience.locations) ? targetAudience.locations : []}
              onChange={(event, newValue) => handleLocationsChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    icon={<PlaceIcon />}
                    label={option}
                    {...getTagProps({ index })}
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Locations *"
                  placeholder="Add location and press Enter"
                  fullWidth
                  required
                  helperText="Cities, countries, or regions (required)"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Platforms Section */}
      {renderPlatformsSection()}
    </>
  );
};

export default DistributionStep;